/**
 * Utilidades DOM para manipulación de interfaz
 * Funciones helper reutilizables en toda la aplicación
 */

/**
 * Selector de elemento único
 * @param {string} selector - Selector CSS
 * @returns {Element|null}
 */
export const el = (selector) => document.querySelector(selector);

/**
 * Selector de múltiples elementos
 * @param {string} selector - Selector CSS
 * @returns {NodeList}
 */
export const els = (selector) => document.querySelectorAll(selector);

/**
 * Mostrar elemento (quita atributo hidden y clase hidden, reestablece display)
 * @param {Element} element - Elemento a mostrar
 */
export const show = (element) => {
  if (!element) return;
  
  // Remover atributo hidden
  element.removeAttribute('hidden');
  
  // Remover clase hidden
  element.classList.remove('hidden');
  
  // Reestablcer display a su valor natural
  element.style.display = '';
  
  // Asegurar que visibility está visible
  if (element.style.visibility === 'hidden') {
    element.style.visibility = 'visible';
  }
};

/**
 * Ocultar elemento (añade atributo hidden y clase hidden con display none)
 * @param {Element} element - Elemento a ocultar
 */
export const hide = (element) => {
  if (!element) return;
  
  // Añadir atributo hidden
  element.setAttribute('hidden', '');
  
  // Añadir clase hidden
  element.classList.add('hidden');
  
  // Forzar display: none en línea
  element.style.display = 'none';
  
  // También ocultar visualmente
  element.style.visibility = 'hidden';
};

/**
 * Limpiar contenido HTML de un elemento
 * @param {Element} element - Elemento a limpiar
 */
export const clear = (element) => {
  if (element) element.innerHTML = '';
};

/**
 * Deshabilitar elemento (botón, input, etc.)
 * @param {Element} element - Elemento a deshabilitar
 */
export const disable = (element) => {
  if (element) {
    element.disabled = true;
    element.setAttribute('aria-disabled', 'true');
  }
};

/**
 * Habilitar elemento
 * @param {Element} element - Elemento a habilitar
 */
export const enable = (element) => {
  if (element) {
    element.disabled = false;
    element.setAttribute('aria-disabled', 'false');
  }
};

/**
 * Abrir modal (dialog nativo)
 * @param {HTMLDialogElement} dialog - Elemento dialog a abrir
 */
export const openModal = (dialog) => {
  if (dialog && typeof dialog.showModal === 'function') {
    dialog.showModal();
    // Gestión de foco para accesibilidad
    const firstFocusable = dialog.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (firstFocusable) {
      firstFocusable.focus();
    }
  }
};

/**
 * Cerrar modal (dialog nativo)
 * @param {HTMLDialogElement} dialog - Elemento dialog a cerrar
 */
export const closeModal = (dialog) => {
  if (dialog && typeof dialog.close === 'function') {
    dialog.close();
  }
};

/**
 * Mostrar notificación toast temporal
 * @param {string} message - Mensaje a mostrar
 * @param {string} type - Tipo: 'success' | 'error' | 'warning' | 'info'
 * @param {number} duration - Duración en ms (default 3000)
 */
export const showToast = (message, type = 'info', duration = 3000) => {
  // Crear contenedor de toasts si no existe
  let toastContainer = el('#toast-container');
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.id = 'toast-container';
    toastContainer.setAttribute('aria-live', 'polite');
    toastContainer.setAttribute('aria-atomic', 'true');
    document.body.appendChild(toastContainer);
  }

  // Crear toast
  const toast = document.createElement('div');
  toast.className = `toast toast--${type}`;
  toast.textContent = message;
  toast.setAttribute('role', 'alert');

  toastContainer.appendChild(toast);

  // Auto-eliminar después de la duración
  setTimeout(() => {
    toast.classList.add('toast--fade-out');
    setTimeout(() => toast.remove(), 300);
  }, duration);
};

/**
 * Copiar texto al portapapeles
 * @param {string} text - Texto a copiar
 * @returns {Promise<boolean>} true si se copió correctamente
 */
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Error al copiar al portapapeles:', error);
    // Fallback para navegadores antiguos
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      textArea.remove();
      return true;
    } catch (e) {
      textArea.remove();
      return false;
    }
  }
};
